import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import SeagullBlack from '../images/04-black.webp';
import SeagullWhite from '../images/03-white.webp';
import SeagullGreen from '../images/byd-green.png';
import PageSeo, {
  articleJsonLd,
  breadcrumbJsonLd,
  keywordSets,
} from '../PageSeo';

function WarnaSeagull() {
  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <PageSeo
        title='Warna BYD Atto 1 NIK 2026 | Black, White & Sprout Green'
        description='Lihat pilihan warna BYD Atto 1 NIK 2026: Cosmos Black, Apricity White, dan Sprout Green. Cek tampilan warna dan ketersediaan unit di BYD BSD Serpong.'
        keywords={[
          ...keywordSets.localDealer,
          'warna BYD Atto 1 NIK 2026',
          'BYD Atto 1 Cosmos Black',
          'BYD Atto 1 Apricity White',
          'BYD Atto 1 Sprout Green',
        ]}
        path='/blog/pilihan-warna-byd-atto1'
        image={SeagullGreen}
        type='article'
        jsonLd={[
          articleJsonLd({
            title: 'Warna BYD Atto 1 NIK 2026',
            description:
              'Panduan pilihan warna BYD Atto 1 NIK 2026 untuk membantu calon pembeli membandingkan Cosmos Black, Apricity White, dan Sprout Green.',
            image: SeagullGreen,
            path: '/blog/pilihan-warna-byd-atto1',
            datePublished: '2025-04-03',
            dateModified: '2026-07-11',
          }),
          breadcrumbJsonLd([
            { name: 'Beranda', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: 'Warna BYD Atto 1', path: '/blog/pilihan-warna-byd-atto1' },
          ]),
        ]}
      />
      <br />
      <br />
      <br />
      <br />
      <Typography variant='h3' component='h1' fontWeight='bold' gutterBottom>
        Pilihan Warna BYD Atto 1 NIK 2026
      </Typography>

      <Typography
        variant='body1'
        paragraph
        sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
      >
        BYD Atto 1 NIK 2026 ditampilkan dalam tiga pilihan warna eksterior:{' '}
        <strong>Cosmos Black, Apricity White, dan Sprout Green</strong>. Halaman
        ini membantu calon pembeli membandingkan karakter setiap warna sebelum
        mengecek stok unit atau melakukan pemesanan.
      </Typography>

      <Grid container spacing={4} sx={{ mt: 4 }}>
        {[
          {
            title: 'BYD Atto 1 Cosmos Black',
            image: SeagullBlack,
            desc: `Cosmos Black memberi tampilan tegas dan elegan. Warna ini cocok untuk pembeli yang menginginkan karakter visual lebih formal dan mudah dipadukan untuk penggunaan harian.`,
          },
          {
            title: 'BYD Atto 1 Apricity White',
            image: SeagullWhite,
            desc: `Apricity White menonjolkan garis bodi Atto 1 dengan tampilan bersih dan modern. Warna terang juga membantu detail eksterior terlihat lebih jelas.`,
          },
          {
            title: 'BYD Atto 1 Sprout Green',
            image: SeagullGreen,
            desc: `Sprout Green memberi kesan segar dan berbeda tanpa terlihat berlebihan. Pilihan ini cocok untuk pembeli yang ingin Atto 1 lebih mudah dikenali di jalan kota.`,
          },
        ].map((color, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper
              elevation={3}
              sx={{ padding: 3, textAlign: 'center', borderRadius: 3 }}
            >
              <Box
                component='img'
                src={color.image}
                alt={color.title}
                sx={{
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'contain',
                  mb: 2,
                }}
              />
              <Typography variant='h5' fontWeight='bold' gutterBottom>
                {color.title}
              </Typography>
              <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                {color.desc}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Kenapa Warna Penting dalam Memilih Mobil?
        </Typography>
        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          Menurut berbagai studi, warna mobil dapat mencerminkan kepribadian dan
          mempengaruhi mood saat berkendara. Pemilik mobil hitam dianggap lebih
          berkelas, sementara pemilik mobil putih lebih perfeksionis dan
          praktis. Warna Sprout Green menunjukkan karakter segar dan modern, sementara
          warna hitam memberi kesan elegan dan tegas.
        </Typography>
        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          Selain itu, warna juga bisa berpengaruh pada nilai jual kembali dan
          visibilitas di jalan. Misalnya, warna putih lebih mudah terlihat di
          malam hari, sementara warna hitam sering terlihat lebih cepat kotor
          namun tetap jadi favorit karena tampilannya yang maskulin dan
          eksklusif.
        </Typography>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Tips Memilih Warna Atto 1
        </Typography>
        <ul style={{ fontSize: '1.1rem', lineHeight: 2 }}>
          <li>
            <strong>Ekstrovert & Ceria:</strong> Pilih <strong>Sprout Green</strong>{' '}
            untuk tampil menonjol tanpa terlalu mencolok
          </li>
          <li>
            <strong>Minimalis & Rapi:</strong> Warna <strong>Putih</strong>{' '}
            cocok banget untuk kamu yang suka hal simpel dan bersih
          </li>
          <li>
            <strong>Stylish & Profesional:</strong> Warna <strong>Hitam</strong>{' '}
            cocok buat kamu yang ingin tampil elegan dan misterius
          </li>
        </ul>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          ❓ Pertanyaan yang Sering Ditanyakan (FAQ)
        </Typography>
        <Box sx={{ mt: 3 }}>
          {[
            {
              q: 'Apakah warna mempengaruhi harga mobil?',
              a: 'Harga dasar umumnya mengikuti varian kendaraan, bukan pilihan warna. Namun, ketersediaan warna dapat berbeda menurut stok unit dan program yang sedang berjalan.',
            },
            {
              q: 'Apa pilihan warna BYD Atto 1 NIK 2026?',
              a: 'Pilihan yang ditampilkan di halaman ini adalah Cosmos Black, Apricity White, dan Sprout Green. Konfirmasi stok warna terbaru sebelum melakukan pemesanan.',
            },
            {
              q: 'Warna apa yang paling mudah dirawat?',
              a: 'Warna putih cenderung mudah dirawat karena tidak terlalu terlihat saat kotor ringan, berbeda dengan warna hitam yang sering menampilkan goresan kecil dan debu.',
            },
          ].map((item, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography variant='h6' fontWeight='bold'>
                {item.q}
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontSize: '1.05rem', lineHeight: 1.8 }}
              >
                {item.a}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* CTA section */}
      <Paper
        sx={{
          p: 4,
          mt: 6,
          backgroundColor: '#e8f5e9',
          borderLeft: '6px solid #43a047',
          borderRadius: 2,
        }}
      >
        <Typography variant='h5' fontWeight='bold' gutterBottom>
          🎨 Sudah Tentukan Warna Favoritmu?
        </Typography>
        <Typography variant='body1' gutterBottom>
          Hubungi kami sekarang dan pilih warna BYD Atto 1 yang paling cocok
          dengan karaktermu!
        </Typography>
        <Typography variant='body2' sx={{ mt: 1 }}>
          📞{' '}
          <a
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20warna%20BYD%20Atto%201%20NIK%202026'
            target='_blank'
            rel='noopener noreferrer'
          >
            Chat WhatsApp
          </a>{' '}
          atau follow kami di{' '}
          <a
            href='https://instagram.com/bydbsdserpong'
            target='_blank'
            rel='noopener noreferrer'
          >
            @bydbsdserpong
          </a>{' '}
          untuk info lebih lanjut dan update terbaru!
        </Typography>
      </Paper>
    </Box>
  );
}

export default WarnaSeagull;
